import React, { useState, useEffect, useRef } from 'react';
import { inject, observer } from 'mobx-react';
import styles from './index.module.scss';
import type { SiteStore } from '@stores/site/store';
import { Spin, Tooltip } from 'antd';
import { getEmojiDisplayValue, isEmojiImageUrl } from '@utils/emoji';

interface EmojiPickerProps {
    visible: boolean;
    onClose: () => void;
    onSelect: (emoji: string, emojiCode?: string, meta?: { category?: string }) => void;
    onDelete?: () => void;
    inFlow?: boolean;
    showEmojiTab?: boolean;
    showCategoryTabs?: boolean;
}

const EmojiPickerH5: React.FC<EmojiPickerProps & { site?: SiteStore }> = ({
    visible,
    onClose,
    onSelect,
    onDelete,
    site,
    inFlow,
    showEmojiTab = false,
    showCategoryTabs = true,
}) => {
    const [activeTab, setActiveTab] = useState<string>('');
    const pickerRef = useRef<HTMLDivElement>(null);
    const maskRef = useRef<HTMLDivElement>(null);
    const deletePressTimeoutRef = useRef<number | null>(null);
    const deletePressIntervalRef = useRef<number | null>(null);
    const ignoreMouseRef = useRef(false);
    const emojiLongPressTimeoutRef = useRef<number | null>(null);
    const [previewEmojiKey, setPreviewEmojiKey] = useState<string | null>(null);

    // 移动端输入框在插入表情后通常会被业务侧重新 focus，导致键盘再次弹出。
    // 表情面板打开期间保持输入框失焦，避免所有使用方分别处理。
    const blurActiveElement = () => {
        const activeElement = document.activeElement;
        if (activeElement instanceof HTMLElement) {
            activeElement.blur();
        }
    };

    useEffect(() => {
        if (!visible) return;

        blurActiveElement();

        const handleClickOutside = (e: TouchEvent | MouseEvent) => {
            if (maskRef.current === e.target) {
                onClose();
            }
        };

        document.addEventListener('touchstart', handleClickOutside);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('touchstart', handleClickOutside);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [visible, onClose]);

    // 如果站点未有表情数据，尝试拉取
    // useEffect(() => {
    //     if (visible && site && !site.hasEmoji && !site.isEmojiLoading) {
    //         (site as any).fetchEmoji?.();
    //     }
    // }, [visible, site]);

    // 初始化 activeTab：当有数据时设置为第一个分类
    useEffect(() => {
        if (site?.emojiList && site.emojiList.length > 0 && !activeTab) {
            setActiveTab(site.emojiList[0].category);
        }
    }, [site?.emojiList, activeTab]);

    useEffect(() => {
        return () => {
            if (deletePressTimeoutRef.current !== null) {
                window.clearTimeout(deletePressTimeoutRef.current);
            }
            if (deletePressIntervalRef.current !== null) {
                window.clearInterval(deletePressIntervalRef.current);
            }
            if (emojiLongPressTimeoutRef.current !== null) {
                window.clearTimeout(emojiLongPressTimeoutRef.current);
            }
        };
    }, []);

    if (!visible) return null;

    const categories = (site?.emojiList as any[]) || [];
    const visibleCategories = categories.filter((cat: any) => showEmojiTab || cat.category !== 'emoji');
    const effectiveCategories = visibleCategories.length > 0 ? visibleCategories : categories;
    let activeCategory = effectiveCategories.find(cat => cat.category === activeTab);
    if (!activeCategory && effectiveCategories.length > 0) {
        activeCategory = effectiveCategories[0];
    }

    const handleEmojiClick = (emojiValue: string, emojiCode: string) => {
        // 选择表情后保持弹窗不关闭，便于连续插入
        onSelect(emojiValue, emojiCode, { category: activeCategory?.category });
        // onSelect 可能在异步回调中重新聚焦输入框，延后一帧再次失焦。
        window.setTimeout(blurActiveElement, 0);
    };

    const handleDelete = () => {
        if (onDelete) {
            onDelete();
        }
        // 保持弹窗不关闭
    };

    const clearDeletePress = () => {
        if (deletePressTimeoutRef.current !== null) {
            window.clearTimeout(deletePressTimeoutRef.current);
            deletePressTimeoutRef.current = null;
        }
        if (deletePressIntervalRef.current !== null) {
            window.clearInterval(deletePressIntervalRef.current);
            deletePressIntervalRef.current = null;
        }
    };

    const startDeletePress = () => {
        clearDeletePress();
        handleDelete();
        deletePressTimeoutRef.current = window.setTimeout(() => {
            deletePressIntervalRef.current = window.setInterval(() => {
                handleDelete();
            }, 80);
        }, 350);
    };

    const handleTouchStart = () => {
        ignoreMouseRef.current = true;
        startDeletePress();
    };

    const handleMouseDown = () => {
        if (ignoreMouseRef.current) return;
        startDeletePress();
    };

    const stopDeletePress = () => {
        clearDeletePress();
        window.setTimeout(() => {
            ignoreMouseRef.current = false;
        }, 0);
    };

    const clearEmojiLongPress = () => {
        if (emojiLongPressTimeoutRef.current !== null) {
            window.clearTimeout(emojiLongPressTimeoutRef.current);
            emojiLongPressTimeoutRef.current = null;
        }
    };

    const startEmojiLongPress = (emojiKey: string) => {
        clearEmojiLongPress();
        emojiLongPressTimeoutRef.current = window.setTimeout(() => {
            setPreviewEmojiKey(emojiKey);
            emojiLongPressTimeoutRef.current = null;
        }, 350);
    };

    const stopEmojiLongPress = () => {
        clearEmojiLongPress();
        setPreviewEmojiKey(null);
    };

    return (
        <>
            {!inFlow && <div className={styles.mask} ref={maskRef}></div>}
            <div className={`${styles.emojiPickerWrap} ${inFlow ? styles.emojiPickerInFlow : ''}`} ref={pickerRef}>
                <Spin spinning={site?.isEmojiLoading}>
                    {showCategoryTabs && (
                        <div className={styles.emojiHeader}>
                            {effectiveCategories.map((category: any) => (
                                <div
                                    key={category.category}
                                    className={`${styles.emojiTab} ${activeTab === category.category ? styles.emojiTabActive : ''}`}
                                    onClick={() => setActiveTab(category.category)}
                                >
                                    {category.category}
                                </div>
                            ))}
                        </div>
                    )}
                    {/* 表情列表 */}
                    <div className={styles.emojiListWrap}>
                        {activeCategory?.emoji?.length ? (
                            activeCategory.emoji.map((emoji: any, index: number) => {
                                const isImageUrl = isEmojiImageUrl(emoji.url);
                                const isDefault = activeCategory.category === '默认';
                                const itemClass = isImageUrl
                                    ? (isDefault ? `${styles.emojiItem} ${styles.default56}` : `${styles.emojiItem} ${styles.vivoEmoji}`)
                                    : `${styles.emojiItem} ${styles.default56}`;
                                const displayValue = getEmojiDisplayValue(emoji);
                                const emojiName = emoji.emoticoNname || emoji.code;
                                const emojiKey = String(emoji.id || emoji.code || index);

                                return (
                                    <Tooltip
                                        key={emojiKey}
                                        title={(
                                            <div className={styles.emojiPreviewContent}>
                                                {isImageUrl ? (
                                                    <img
                                                        src={emoji.url}
                                                        alt={emojiName}
                                                        className={styles.emojiPreviewImage}
                                                    />
                                                ) : (
                                                    <span className={styles.emojiPreviewText}>{displayValue}</span>
                                                )}
                                                <span className={styles.emojiPreviewName}>{emojiName}</span>
                                            </div>
                                        )}
                                        open={previewEmojiKey === emojiKey}
                                        trigger={[]}
                                        placement="top"
                                        arrow={false}
                                        overlayClassName={styles.emojiPreviewTooltip}
                                    >
                                        <div
                                            className={itemClass}
                                            onClick={() => handleEmojiClick(displayValue, emoji.code)}
                                            onTouchStart={() => startEmojiLongPress(emojiKey)}
                                            onTouchEnd={stopEmojiLongPress}
                                            onTouchCancel={stopEmojiLongPress}
                                            onTouchMove={stopEmojiLongPress}
                                            onContextMenu={event => event.preventDefault()}
                                        >
                                            {isImageUrl ? (
                                                <img
                                                    src={emoji.url}
                                                    alt={emojiName}
                                                    className={isDefault ? styles.emojiImgSmall : styles.emojiImgLarge}
                                                />
                                            ) : (
                                                <span className={styles.emojiText}>{displayValue}</span>
                                            )}
                                        </div>
                                    </Tooltip>
                                );
                            })
                        ) : (
                            <div className={styles.empty}>暂无表情</div>
                        )}
                    </div>
                    {/* 删除按钮 */}
                    <div
                        className={styles.deleteBtn}
                        onTouchStart={handleTouchStart}
                        onTouchEnd={stopDeletePress}
                        onTouchCancel={stopDeletePress}
                        onMouseDown={handleMouseDown}
                        onMouseUp={stopDeletePress}
                        onMouseLeave={stopDeletePress}
                    >
                        <img
                            src="https://vivo-oss-sz-7026.oss-cn-shenzhen.aliyuncs.com/images/vivo-pc/Icon%403x.png"
                            alt="删除"
                            className={styles.deleteIcon}
                        />
                    </div>
                </Spin>
            </div>
        </>
    );
};

export default inject('site')(observer(EmojiPickerH5));
